import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, Eye, MousePointerClick, Send, TrendingUp, TrendingDown, Minus, Loader2 } from "lucide-react";

const RANGES = [7, 30, 90];

function Delta({ change }) {
  if (change === null || change === undefined) return <span className="text-xs text-slate-400">no prior data</span>;
  const up = change > 0, flat = change === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const color = flat ? "text-slate-400" : up ? "text-emerald-600" : "text-red-500";
  return <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}><Icon size={13} />{Math.abs(change)}% vs prev</span>;
}

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-navy text-brand-cyan"><Icon size={16} /></span>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-navy">{value}</div>
      {sub && <div className="mt-1">{sub}</div>}
    </div>
  );
}

function Trend({ data }) {
  if (!data.length) return <div className="text-slate-400 text-sm py-10 text-center">No traffic data yet for this period.</div>;
  const w = 720, h = 180, pad = 8;
  const max = Math.max(1, ...data.map((d) => d.pageviews));
  const step = data.length > 1 ? (w - pad * 2) / (data.length - 1) : 0;
  const pt = (d, i) => [pad + i * step, h - pad - (d.pageviews / max) * (h - pad * 2)];
  const line = data.map((d, i) => pt(d, i).join(",")).join(" ");
  const area = `${pad},${h - pad} ${line} ${pad + (data.length - 1) * step},${h - pad}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-44" data-testid="analytics-trend">
      <defs><linearGradient id="tg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity="0.35" /><stop offset="100%" stopColor="#2563EB" stopOpacity="0" /></linearGradient></defs>
      <polygon points={area} fill="url(#tg)" />
      <polyline points={line} fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinejoin="round" />
      {data.map((d, i) => { const [x, y] = pt(d, i); return <circle key={i} cx={x} cy={y} r="2.5" fill="#2563EB" />; })}
    </svg>
  );
}

function BarList({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <h3 className="font-semibold text-navy mb-4">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-slate-400">No data yet.</p> : (
        <ul className="space-y-2.5">
          {rows.map((r, i) => (
            <li key={i}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-700 truncate pr-3">{r.label}</span>
                <span className="text-slate-500 font-mono">{r.count}</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-brand-blue" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Funnel({ f }) {
  const stages = [
    { key: "visitors", label: "Visitors" }, { key: "cta_click", label: "CTA Clicks" },
    { key: "quote_start", label: "Quote Started" }, { key: "quote_submit", label: "Quote Submitted" },
  ];
  const top = Math.max(1, f.visitors);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="analytics-funnel">
      <h3 className="font-semibold text-navy mb-4">Conversion Funnel</h3>
      <div className="space-y-3">
        {stages.map((s, i) => {
          const val = f[s.key] || 0;
          const prev = i === 0 ? val : (f[stages[i - 1].key] || 0);
          const rate = prev ? Math.round((val / prev) * 100) : 0;
          return (
            <div key={s.key}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-700">{s.label}</span>
                <span className="text-slate-500 font-mono">{val}{i > 0 && <span className="text-slate-400"> · {rate}%</span>}</span>
              </div>
              <div className="h-8 rounded-lg bg-slate-100 overflow-hidden">
                <div className="h-full rounded-lg bg-gradient-to-r from-navy to-brand-blue flex items-center px-3 text-white text-xs font-medium"
                  style={{ width: `${Math.max(6, (val / top) * 100)}%` }}>{val}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [days]);

  if (!data && loading) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="animate-spin" size={18} /> Loading analytics…</div>;
  if (!data) return <p className="text-slate-400">No analytics available.</p>;

  const c = data.comparison;
  return (
    <div data-testid="admin-analytics-view">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-navy">Website Analytics</h2>
          <p className="text-slate-500 text-sm mt-1">Privacy-conscious, aggregated traffic and lead conversion.</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setDays(r)} data-testid={`analytics-range-${r}`}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${days === r ? "bg-brand-blue text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {r}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Visitors (today)" value={data.kpi.visitors_today} sub={<span className="text-xs text-slate-400">{data.kpi.visitors_all} all-time</span>} />
        <Kpi icon={Eye} label="Page views (today)" value={data.kpi.pageviews_today} sub={<span className="text-xs text-slate-400">{data.kpi.pageviews_all} all-time</span>} />
        <Kpi icon={MousePointerClick} label={`Visitors (${days}d)`} value={c.visitors.current} sub={<Delta change={c.visitors.change} />} />
        <Kpi icon={Send} label={`Quote submissions (${days}d)`} value={c.submissions.current} sub={<Delta change={c.submissions.change} />} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Kpi icon={Users} label="Unique visitors" value={data.range.unique} sub={<span className="text-xs text-slate-400">{data.range.new} new · {data.range.returning} returning</span>} />
        <Kpi icon={Eye} label={`Page views (${days}d)`} value={data.range.pageviews} sub={<Delta change={c.pageviews.change} />} />
        <Kpi icon={Send} label={`Leads (${days}d)`} value={data.total_leads} />
        <Kpi icon={MousePointerClick} label="Visitors (7d / 30d)" value={`${data.kpi.visitors_7d} / ${data.kpi.visitors_30d}`} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 mt-6">
        <h3 className="font-semibold text-navy mb-2">Daily traffic ({days} days)</h3>
        <Trend data={data.trend} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <Funnel f={data.funnel} />
        <BarList title="Lead sources (attribution)" rows={data.lead_sources} testid="analytics-lead-sources" />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <BarList title="Traffic sources" rows={data.sources} testid="analytics-sources" />
        <BarList title="Top pages" rows={data.pages} testid="analytics-pages" />
        <BarList title="Devices" rows={data.devices} testid="analytics-devices" />
        <BarList title="UTM sources" rows={data.utm_sources} testid="analytics-utm-sources" />
        <BarList title="UTM campaigns" rows={data.utm_campaigns} testid="analytics-utm-campaigns" />
        <BarList title="Locations (country)" rows={data.locations} testid="analytics-locations" />
      </div>
    </div>
  );
}
